import React from "react"
import {
	handleInputChange,
	HandleInputChange,
	handleInputValidation,
	HandleInputValidation,
	formStatusEnum,
	FormStatusEnumValues,
} from "components/Form/formUtils"
import { FormError } from "models"

type DiscardChangesProps = {
	formHasChanged: boolean
	refreshData?: () => Promise<void>
}

export type FormSharedUtils<T> = {
	formFields: T
	setFormFields: React.Dispatch<React.SetStateAction<T>>
	formStatus: FormStatusEnumValues
	setFormStatus: React.Dispatch<React.SetStateAction<FormStatusEnumValues>>
	handleChange: HandleInputChange
	errors: Array<FormError>
	showLocalErrors: boolean
	setShowLocalErrors: React.Dispatch<React.SetStateAction<boolean>>
	showGlobalError: boolean
	setShowGlobalError: React.Dispatch<React.SetStateAction<boolean>>
	handleValidation: HandleInputValidation
	discardChanges: (props: DiscardChangesProps) => Promise<void>
}

const useForm = ({
	defaultFormFields = undefined,
	defaultFormStatus = formStatusEnum.ready,
}: {
	defaultFormFields?: FormSharedUtils<any>["formFields"]
	defaultFormStatus?: FormStatusEnumValues
	// TODO: if you see this, do not copy/paste the `any` type.
	// use instead `unknwon` and type each form using it.
}): FormSharedUtils<any> => {
	const [formFields, setFormFields] = React.useState<any>(defaultFormFields)
	const [formStatus, setFormStatus] = React.useState<FormStatusEnumValues>(defaultFormStatus)
	const handleChange = React.useCallback(
		(event:React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => handleInputChange(event, setFormFields),
		[]
	)

	const [errors, setErrors] = React.useState<FormError[]>([])
	const [showLocalErrors, setShowLocalErrors] = React.useState<boolean>(false)
	const [showGlobalError, setShowGlobalError] = React.useState<boolean>(false)
	const handleValidation = React.useCallback(
		(name: string, errs: FormError[]): void => handleInputValidation(name, errs, setErrors),
		[]
	)

	const discardChanges = async ({ formHasChanged, refreshData }: DiscardChangesProps) => {
		setFormStatus(formStatusEnum.readonly)
		if (formHasChanged) await refreshData()
	}

	return {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		showGlobalError,
		setShowGlobalError,
		handleValidation,
		discardChanges,
	}
}

export { useForm }
